import { gql, useLazyQuery } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import { useNavigate } from 'react-router';

import { CardPackCurrency } from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_SERIES_MARKET,
  ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { COMPETITION_COLORS } from '@sorare/core/src/lib/colors';
import { useGetSpecialSetCardPackGroupsThatAreNotFeatured } from '@sorare/core/src/set';

import type {
  CommonEssenceRewardedQuery,
  CommonEssenceRewardedQueryVariables,
} from './__generated__/useOnCommonEssenceClick.graphql.ts';

const CommonEssenceRewardedQuery = gql`
  query CommonEssenceRewardedQuery($sport: Sport!) {
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        favoriteTeam {
          slug
          activeCompetitions {
            slug
          }
        }
      }
    }
    market {
      id
      cardPackGroups(sport: $sport) {
        id
        cardPacks {
          slug
          currency
          competition {
            slug
          }
        }
        ...getSpecialSetCardPackGroupsThatAreNotFeatured_cardPackGroup
      }
    }
  }
  ${useGetSpecialSetCardPackGroupsThatAreNotFeatured.fragments.cardPackGroup}
` as TypedDocumentNode<
  CommonEssenceRewardedQuery,
  CommonEssenceRewardedQueryVariables
>;

const COMMMON_GAME_AVAILABLE_COMPETITIONS = Object.keys(COMPETITION_COLORS);

export const useOnCommonEssenceClick = (isFirstEssenceReward: boolean) => {
  const navigate = useNavigate();
  const { sport, generateSportContextPath } = useSportContext();
  const [getData] = useLazyQuery(CommonEssenceRewardedQuery);
  const getSpecialSetCardPackGroupsThatAreNotFeatured =
    useGetSpecialSetCardPackGroupsThatAreNotFeatured();
  return async () => {
    if (!isFirstEssenceReward) {
      navigate(generateSportContextPath(ANY_SPORT_SERIES_MARKET));
      return;
    }
    const { data } = await getData({ variables: { sport } });
    const competitions =
      data?.currentUser?.anyUserSportProfile?.favoriteTeam?.activeCompetitions;

    const commonGameCompetition = competitions?.find(competition =>
      COMMMON_GAME_AVAILABLE_COMPETITIONS.includes(competition.slug)
    );

    const { cardPackGroups } = data?.market || {};

    const specialSetGroups = cardPackGroups
      ? getSpecialSetCardPackGroupsThatAreNotFeatured(cardPackGroups)
      : [];
    const correspondingPackGroup = specialSetGroups.find(group =>
      group.cardPacks.some(
        pack => pack.competition?.slug === commonGameCompetition?.slug
      )
    );
    const essencePackSlug = correspondingPackGroup?.cardPacks.find(
      pack => pack.currency === CardPackCurrency.COMMON_ESSENCE
    )?.slug;

    if (correspondingPackGroup && essencePackSlug) {
      navigate(
        `${generateSportContextPath(
          ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP,
          {
            params: { group: idFromObject(correspondingPackGroup.id) },
          }
        )}?pack=${idFromObject(essencePackSlug)}`
      );
      return;
    }

    navigate(generateSportContextPath(ANY_SPORT_SERIES_MARKET));
  };
};
